<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { HostFromVideo, useQuizterStore } from '@ilteducation/games';
import { useRoute, useRouter } from 'vue-router';
import { useVideos } from '@ilteducation/videos';
import { useLocale, useLocalisation } from '@/plugins/translation';
import { useConfigHelper } from '@ilteducation/config';

const route = useRoute();
const router = useRouter();
const videoId = route.params.videoId?.toString() ?? '';
const { t } = useLocale();
const countryCode = useConfigHelper().getRealm();

// Load Quizter translations from the Quizter Lokalise project
const { includeAdditionalProject } = useLocalisation();
includeAdditionalProject('Quizter', false);

const videoStore = useVideos();
const videoTitle = ref<string>();
const videoThumbnail = ref<string>();

// Extract the raw video ID (without market suffix)
const rawVideoId = computed(() => videoId.replace(new RegExp(`-${countryCode}$`), ''));

// Capture sourceUrl once at initialization (before stripping query params)
const sourceUrl = route.query.from?.toString() ?? `/video/${rawVideoId.value}`;

// Make the URL absolute so it works when players are on a different domain (e.g., quizter.gg)
const absoluteSourceUrl = sourceUrl.startsWith('http') ? sourceUrl : `${window.location.origin}${sourceUrl}`;

const returnParams = computed(() => ({
  url: absoluteSourceUrl,
  text: t('quizter.back_to_video'),
}));

// Store for game creation
const quizterStore = useQuizterStore();

// Fetch video data to get the title and thumbnail
watch(
  rawVideoId,
  async (id) => {
    if (id) {
      const video = await videoStore.getVideoById(id);
      videoTitle.value = video?.title;
      videoThumbnail.value = video?.thumbnail || video?.previewImage;
    }
  },
  { immediate: true },
);

// Strip query params from URL after capturing them
onMounted(() => {
  // Connect WebSocket so HostFromVideo can check isConnected
  quizterStore.reset();
  quizterStore.connect();

  if (Object.keys(route.query).length > 0) {
    router.replace({ query: {} });
  }
});

const goBack = () => {
  router.push(sourceUrl);
};

const handleStart = async (resourceIds: string[]) => {
  if (resourceIds.length === 0) {
    return;
  }

  // Create the game and get the gameId
  await quizterStore.createGame(resourceIds, false, undefined, undefined, returnParams.value);

  // Navigate to the host route with the gameId
  const gameId = quizterStore.gameId;
  if (gameId) {
    router.push({
      name: 'HostQuizter',
      params: {
        gameId,
        returnUrl: returnParams.value.url,
        returnText: returnParams.value.text,
      },
    });
  }
};
</script>

<template>
  <HostFromVideo
    :resource-id="videoId"
    :source-name="videoTitle"
    :source-thumbnail="videoThumbnail"
    @start="handleStart"
    @back="goBack"
  />
</template>

<style scoped lang="scss"></style>
